import React from 'react';
import { useMemoryGame } from './MemoryGameContext';
import { useAuth } from '../../context/FirebaseAuthContext';
import { useGameSession } from '../../context/GameSessionContext';
import { GameResultSaver } from '../../utils/gameResultSaver';
import { GameStats } from './types';
import { exportToCSV, calculateDynamicDifficulty } from './utils/gameUtils';

const getMotivationalMessage = (accuracy: number): string => {
  if (accuracy >= 90) return "Outstanding memory! 🌟";
  if (accuracy >= 70) return "Great job! Keep practicing! 👍";
  if (accuracy >= 50) return "Good effort! You're improving! 💪";
  return "Keep trying! Practice makes perfect! 🎯";
};

interface ResultsTableProps {
  history: GameStats[];
}

const ResultsTable: React.FC<ResultsTableProps> = ({ history }) => (
  <div className="mt-6 overflow-x-auto">
    <table className="min-w-full bg-white rounded-lg overflow-hidden">
      <thead className="bg-gray-100">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Date
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Accuracy
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Time
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Score
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {history.map((stat, index) => (
          <tr key={index}>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {new Date(stat.timestamp).toLocaleString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {stat.accuracy.toFixed(1)}%
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {stat.timeSeconds}s
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {stat.score}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ResultScreen: React.FC = () => {
  const { state, dispatch } = useMemoryGame();
  const { user } = useAuth();
  const { currentSession } = useGameSession();
  const { stats, difficulty } = state;

  // Save result to history and apply DDA
  React.useEffect(() => {
    if (stats) {
      // Save to local storage (existing functionality)
      const history = JSON.parse(localStorage.getItem('memoryGameHistory') || '[]');
      const updatedStats = { ...stats, difficulty }; // Include difficulty in stats
      history.push(updatedStats);
      localStorage.setItem('memoryGameHistory', JSON.stringify(history));

      // Save to database via API
      const saveToDatabase = async () => {
        try {
          const apiData = {
            sessionId: currentSession?.sessionId || `session-${Date.now()}`,
            timestamp: new Date().toISOString(),
            accuracyPercentage: stats.accuracy,
            timeSeconds: stats.timeSeconds,
            score: stats.score,
            difficultyLevel: difficulty,
            objectsCount: state.objects?.length || 0,
            gridSize: difficulty <= 3 ? 3 : difficulty <= 6 ? 4 : 5, // Estimate grid size based on difficulty
            memorizationTimeSeconds: 10, // Default memorization time
            correctPlacements: Math.round((stats.accuracy / 100) * (state.objects?.length || 0)),
            totalPlacements: state.objects?.length || 0,
            objectPositions: JSON.stringify(state.objects?.map(obj => ({ 
              id: obj.id, 
              x: obj.position.x, 
              y: obj.position.y, 
              type: obj.type 
            })) || []),
            playerPlacements: JSON.stringify(state.playerPlacements?.map(obj => ({ 
              id: obj.id, 
              x: obj.position.x, 
              y: obj.position.y, 
              type: obj.type 
            })) || [])
          };

          console.log('Saving MemoryPalace result to Firebase:', apiData);
          await GameResultSaver.saveMemoryPalaceResult(apiData);
          console.log('MemoryPalace result saved successfully to Firebase');
        } catch (error) {
          console.error('Failed to save MemoryPalace result to database:', error);
        }
      };

      saveToDatabase();

      // Calculate new difficulty based on performance
      const newDifficulty = calculateDynamicDifficulty(history);
      if (newDifficulty !== difficulty) {
        dispatch({
          type: 'RESET_GAME',
          payload: { difficulty: newDifficulty }
        });
      }
    }
  }, [stats, difficulty, dispatch, currentSession, state.objects, state.playerPlacements]);

  if (!stats) return null;

  const history = JSON.parse(localStorage.getItem('memoryGameHistory') || '[]') as GameStats[];
  
  const handleExportCSV = () => {
    exportToCSV(history);
  };

  const handleNextLevel = () => {
    dispatch({ type: 'RESET_GAME' });
  };

  const handleRetry = () => {
    dispatch({ type: 'RESET_GAME', payload: { difficulty } });
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-3xl font-bold text-center mb-6">Level Complete!</h2>
      
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="text-center bg-blue-50 p-4 rounded-lg">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {stats.accuracy.toFixed(1)}%
          </div>
          <div className="text-gray-600">Accuracy</div>
        </div>
        <div className="text-center bg-green-50 p-4 rounded-lg">
          <div className="text-3xl font-bold text-green-600 mb-2">
            {stats.score}
          </div>
          <div className="text-gray-600">Score</div>
        </div>
        <div className="text-center bg-purple-50 p-4 rounded-lg">
          <div className="text-3xl font-bold text-purple-600 mb-2">
            {stats.timeSeconds}s
          </div>
          <div className="text-gray-600">Time</div>
        </div>
      </div>

      {/* Debug information to help track accuracy */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold mb-2">Placement Results:</h3>
        <div className="text-sm">
          <p>Objects to place: {state.objects?.length || 0}</p>
          <p>Objects placed: {state.playerPlacements?.length || 0}</p>
          <p>Correct placements: {Math.round((stats.accuracy / 100) * (state.objects?.length || 0))}</p>
          <p>Incorrect placements: {(state.objects?.length || 0) - Math.round((stats.accuracy / 100) * (state.objects?.length || 0))}</p>
        </div>
      </div>

      <div className="text-center mb-8">
        <p className="text-xl text-gray-700">
          {getMotivationalMessage(stats.accuracy)}
        </p>
        <p className="text-blue-600 mt-2">
          Current Difficulty Level: {difficulty}
        </p>
      </div>

      <div className="flex justify-center space-x-4">
        <button
          onClick={handleRetry}
          className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Try Again
        </button>
        <button
          onClick={handleNextLevel}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Continue
        </button>
        <button
          onClick={handleExportCSV}
          className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
        >
          Export Results
        </button>
      </div>

      <ResultsTable history={history} />
    </div>
  );
};